import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getEncumbranceTabName } from '@core/util/encumbrance';
import { Business } from '@models/businesses';
import {
	defaultPaymentCalc,
	DischargeRegisteredByType,
	Encumbrance,
	EncumbranceAdditionalAmount,
	EncumbranceInterestType,
} from '@models/encumbrance';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { EncumbrancesDispatch } from '@state/matter/encumbrances';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import {
	calculateEncumbranceTotal,
	EncumbranceCalculated,
} from 'src/app/shared/services/financialTotals/financial-totals.service';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { arrayUnion } from '@angular/fire/firestore';

const interestTypeOptions: RadioGroupItem<EncumbranceInterestType>[] = [
	{
		label: 'Per diem interest',
		value: EncumbranceInterestType.PER_DIEM_INTEREST,
	},
	{ label: 'Annual rate', value: EncumbranceInterestType.ANNUAL_RATE },
];

@Component({
	selector: 'app-payment-calculator-side-form',
	templateUrl: './payment-calculator-side-form.component.html',
	standalone: false,
})
export class PaymentCalculatorSideFormComponent {
	public encumbrance: Encumbrance;
	public ICON = ICON;
	public interestRateOptions = interestTypeOptions;
	public interestRateTypes = EncumbranceInterestType;
	public dischargeOptions: RadioGroupItem<DischargeRegisteredByType>[] = [
		{ label: 'law firm', value: DischargeRegisteredByType.LAWYER },
		{ label: 'Mortgagee', value: DischargeRegisteredByType.MORTGAGEE },
	];

	public principalMinDate: string;
	public principalMaxDate: string;

	public total: EncumbranceCalculated;

	@Select(BusinessState.getBusiness) business$: Observable<Business>;

	constructor(
		private router: Router,
		private navigationService: NavigationService,
		private route: ActivatedRoute,
		public encumbranceDispatch: EncumbrancesDispatch,
	) {
		this.encumbrance =
			this.router.getCurrentNavigation()?.extras.state?.encumbrance;
		if (this.encumbrance) {
			const _encumbrance = cloneDeep(this.encumbrance);

			if (!_encumbrance.paymentCalc) {
				_encumbrance.paymentCalc = cloneDeep(defaultPaymentCalc);
			}
			this.encumbrance = _encumbrance;
			this.principalMinDate = this.encumbrance.paymentCalc.principalAt ?? '';
			this.principalMaxDate = this.encumbrance.paymentCalc.principalFrom ?? '';
			this.updateTotal();
			this.business$.pipe(take(1)).subscribe((business) => {
				this.dischargeOptions = [
					{ label: business.name, value: DischargeRegisteredByType.LAWYER },
					{ label: 'Mortgagee', value: DischargeRegisteredByType.MORTGAGEE },
				];
			});
		} else {
			this.navigationService.goBack(this.route);
		}
	}

	addAdditionalAmount() {
		const additionAmount: EncumbranceAdditionalAmount = {
			description: '',
			amount: 0,
		};

		this.encumbrance?.paymentCalc.additionalAmounts.push(additionAmount);
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.additionalAmounts',
			arrayUnion(additionAmount),
		);
	}

	removeAdditionalAmount(index: number) {
		this.encumbrance?.paymentCalc.additionalAmounts.splice(index, 1);
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.additionalAmounts',
			this.encumbrance.paymentCalc.additionalAmounts,
		);
		this.updateTotal();
	}

	updateTotal(): void {
		this.total = calculateEncumbranceTotal(this.encumbrance);
	}

	trackByAdditionalAmount = (index: number) => index;

	cancel() {
		this.navigationService.goBack(this.route);
	}

	get title() {
		return this.encumbrance ? getEncumbranceTabName(this.encumbrance) : '';
	}

	updatePrincipalAt(value: string): void {
		this.encumbrance.paymentCalc.principalAt = value;
		this.principalMinDate = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.principalAt',
			value,
		);
		this.updateTotal();
	}

	updatePrincipalFrom(value: string): void {
		this.encumbrance.paymentCalc.principalFrom = value;
		this.principalMaxDate = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.principalFrom',
			value,
		);
		this.updateTotal();
	}

	updatePrincipalAmount(value: number) {
		this.encumbrance.paymentCalc.principalAmount = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.principalAmount',
			value,
		);
		this.updateTotal();
	}

	updateInterestType(value: EncumbranceInterestType) {
		this.encumbrance.paymentCalc.interestType = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.interestType',
			value,
		);
		this.updateTotal();
	}

	updateAdditionalDesc(index: number, description: string) {
		this.encumbrance.paymentCalc.additionalAmounts[index].description =
			description;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.additionalAmounts',
			this.encumbrance.paymentCalc.additionalAmounts,
		);
		this.updateTotal();
	}

	updateAdditionalAmount(index: number, amount: number) {
		this.encumbrance.paymentCalc.additionalAmounts[index].amount = amount;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.additionalAmounts',
			this.encumbrance.paymentCalc.additionalAmounts,
		);
		this.updateTotal();
	}

	updateInterest(value: number) {
		this.encumbrance.paymentCalc.interest = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.interest',
			value,
		);
		this.updateTotal();
	}

	updateDischargeBy(value: DischargeRegisteredByType) {
		this.encumbrance.paymentCalc.dischargeRegisteredBy = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.dischargeRegisteredBy',
			value,
		);
		this.updateTotal();
	}

	updateFee(value: number) {
		this.encumbrance.paymentCalc.fee = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.fee',
			value,
		);
		this.updateTotal();
	}

	updateSeparateCheque(value: boolean) {
		this.encumbrance.paymentCalc.separateCheque = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.separateCheque',
			value,
		);
		this.updateTotal();
	}

	updatePayableTo(value: string) {
		this.encumbrance.paymentCalc.payableTo = value;
		this.encumbranceDispatch.UpdateEncumbrance(
			this.encumbrance.id,
			'paymentCalc.payableTo',
			value,
		);
	}
}
